/**
 * "Get the Neosocial app for Windows" (web only, 2026-11-01): a slim glass
 * banner at the top with Download and a close (X) that hides it for 30 days
 * on this browser. Who sees it: lib/desktopDownload.ts. It sits in the
 * banners' own row (ConfigBanners), so it pushes the app down and never
 * covers it.
 */
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { desktopBridge } from '../lib/config';
import {
  browserHints,
  fetchDownloadRelease,
  readDismissedAt,
  shouldOfferDownload,
  writeDismissedAt,
  type DownloadRelease,
} from '../lib/desktopDownload';
import { useFlag } from '../store/config';
import { Icon, IconBtn } from '../ui/kit';

/** One manifest request per page load, shared by every mount (sign-in, then the app). */
let releaseAsk: Promise<DownloadRelease | null> | null = null;

/** For tests: forget the manifest answer. */
export function resetDownloadRelease(): void {
  releaseAsk = null;
}

function askRelease(): Promise<DownloadRelease | null> {
  releaseAsk ??= fetchDownloadRelease(location.origin);
  return releaseAsk;
}

/** The release to offer, or null while the banner must not show. */
export function useDesktopDownload(): { release: DownloadRelease | null; dismiss(): void } {
  const flagOn = useFlag('desktop_download_banner');
  const [dismissedAt, setDismissedAt] = useState(readDismissedAt);
  const [release, setRelease] = useState<DownloadRelease | null>(null);
  // the page's load time is enough for a 30-day snooze (and keeps render pure)
  const [loadedAt] = useState(() => Date.now());
  const offer = shouldOfferDownload({
    hints: browserHints(),
    inDesktopApp: !!desktopBridge(),
    flagOn,
    dismissedAt,
    now: loadedAt,
  });
  useEffect(() => {
    if (!offer) return;
    let live = true;
    void askRelease().then((r) => {
      if (live) setRelease(r);
    });
    return () => {
      live = false;
    };
  }, [offer]);
  return {
    release: offer ? release : null,
    dismiss: () => {
      const now = Date.now();
      writeDismissedAt(now);
      setDismissedAt(now);
    },
  };
}

export function DesktopDownloadBar({
  release,
  onDismiss,
}: {
  release: DownloadRelease;
  onDismiss: (btn: HTMLElement) => void;
}) {
  const { t } = useT();
  return (
    <div
      className="ns-cfg-bar ns-dl-bar nx-raised"
      data-style="download"
      data-dl-version={release.version}
      role="region"
      aria-label={t('dl.bar.label')}
    >
      <Icon name="download" />
      <div className="ns-cfg-bar-t">
        <b>{t('dl.bar.title')}</b>
        <span>{t('dl.bar.body')}</span>
      </div>
      <a className="nx-btn primary xs ns-dl-go" href={release.url} download>
        <Icon name="download" />
        <span className="lbl">{t('dl.bar.download')}</span>
      </a>
      <IconBtn
        icon="x"
        className="ns-cfg-x"
        label={t('dl.bar.hide')}
        onClick={(e) => onDismiss(e.currentTarget)}
      />
    </div>
  );
}
